import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { format } from 'date-fns';
import { LockKeyhole, PencilLine, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { formatJournalEntryType } from '@/pages/developer/types';
import { INTERNAL_CUSTOMER_ID } from '@/pages/developer/types';
import type { Customer, JournalEntry, TimeEntry } from '@/pages/developer/types';

const CREATE_CUSTOMER_VALUE = '__create_customer__';

export type InvoicesCardProps = {
  developerUnlocked: boolean;
  isFirebaseConfigured: boolean;

  workorders: TimeEntry[];
  defaultCommitRate: number;
  closeGitOrder: (gitOrderId: string) => void;
  openInvoiceForGitOrder: (gitOrderId: string) => void;
  openWorkorderCommitsDialog: (gitOrderId: string) => void;
  deleteGitOrder: (gitOrderId: string) => void;

  journalDate: string;
  setJournalDate: Dispatch<SetStateAction<string>>;
  customers: Customer[];
  journalCustomerId: string;
  setJournalCustomerId: Dispatch<SetStateAction<string>>;
  journalAmount: string;
  setJournalAmount: Dispatch<SetStateAction<string>>;
  journalMisc: string;
  setJournalMisc: Dispatch<SetStateAction<string>>;
  journalTaxes: string;
  setJournalTaxes: Dispatch<SetStateAction<string>>;
  journalType: JournalEntry['entryType'];
  setJournalType: Dispatch<SetStateAction<JournalEntry['entryType']>>;
  journalNotes: string;
  setJournalNotes: Dispatch<SetStateAction<string>>;

  createCustomer: (name: string) => Promise<string | null>;

  handleAddJournalEntry: () => void;
  onOpenDeveloperDialog: () => void;

  invoiceEntries: Array<JournalEntry & { amount: number }>;
  totalInvoiceAmount: number;
  openInvoiceEditor: (entryId: string) => void;
};

export function InvoicesCard({
  developerUnlocked,
  isFirebaseConfigured,
  workorders,
  defaultCommitRate,
  closeGitOrder,
  openInvoiceForGitOrder,
  openWorkorderCommitsDialog,
  deleteGitOrder,
  journalDate,
  setJournalDate,
  customers,
  journalCustomerId,
  setJournalCustomerId,
  journalAmount,
  setJournalAmount,
  journalMisc,
  setJournalMisc,
  journalTaxes,
  setJournalTaxes,
  journalType,
  setJournalType,
  journalNotes,
  setJournalNotes,
  createCustomer,
  handleAddJournalEntry,
  onOpenDeveloperDialog,
  invoiceEntries,
  totalInvoiceAmount,
  openInvoiceEditor,
}: InvoicesCardProps) {
  const [newCustomerName, setNewCustomerName] = useState('');
  const visibleCustomers = useMemo(() => customers.filter((customer) => customer.id !== INTERNAL_CUSTOMER_ID), [customers]);

  const handleCreateCustomer = async () => {
    const name = newCustomerName.trim();
    if (!name) return;
    const createdId = await createCustomer(name);
    if (!createdId) return;
    setNewCustomerName('');
    setJournalCustomerId(createdId);
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PencilLine className="w-5 h-5" />
          Invoices
        </CardTitle>
        <CardDescription>View invoice activity and account balance details.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6 rounded-lg border border-border/60 bg-muted/10 p-4">
          <p className="text-sm font-medium">Git orders</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Git orders are service drafts. Close a Git order to link it to an invoice (an invoice will be created if needed).
          </p>

          <div className="mt-3 rounded-lg border border-border overflow-hidden">
            <div className="sm:hidden">
              {workorders.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">No Git orders yet.</div>
              ) : (
                <div className="divide-y divide-border">
                  {workorders.map((entry) => {
                    const commitCount = (entry.commitShas ?? []).length;
                    const unitRate = typeof entry.commitRate === 'number' ? entry.commitRate : defaultCommitRate;
                    const miscValue = typeof entry.misc === 'number' ? entry.misc : 0;
                    const taxesValue = typeof entry.taxes === 'number' ? entry.taxes : 0;
                    const totalValue = commitCount * unitRate + miscValue + taxesValue;
                    const hoursValue = typeof entry.hours === 'number' && Number.isFinite(entry.hours) ? entry.hours : 0;
                    const hasInvoice = !!entry.invoiceJournalEntryId;
                    const isClosed = hasInvoice || entry.stage === 'invoiced';

                    return (
                      <div key={entry.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">
                              {Number.isNaN(new Date(entry.date).getTime()) ? '—' : format(new Date(entry.date), 'MMM d, yyyy')}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground break-words">{entry.description || '—'}</p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              Hours {hoursValue ? hoursValue.toFixed(2) : '—'} · Qty {commitCount || '—'} · Rate {commitCount > 0 ? unitRate.toFixed(2) : '—'}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Misc {miscValue ? miscValue.toFixed(2) : '—'} · Taxes {taxesValue ? taxesValue.toFixed(2) : '—'}
                            </p>
                          </div>

                          <div className="shrink-0 text-right">
                            <p className="text-sm font-semibold">
                              {commitCount > 0 || miscValue || taxesValue ? totalValue.toFixed(2) : '—'}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {isClosed ? 'Closed' : 'Draft'}
                            </p>
                          </div>
                        </div>

                        {developerUnlocked && (
                          <div className="mt-3">
                            {isClosed ? (
                              <div className="flex flex-col gap-2">
                                <Button className="w-full" variant="outline" size="sm" onClick={() => openInvoiceForGitOrder(entry.id)}>
                                  Open invoice
                                </Button>
                                <Button className="w-full gap-2" variant="outline" size="sm" onClick={() => deleteGitOrder(entry.id)}>
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </Button>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-2">
                                <Button
                                  className="w-full gap-2"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openWorkorderCommitsDialog(entry.id)}
                                >
                                  <PencilLine className="w-4 h-4" />
                                  Edit
                                </Button>
                                <Button className="w-full" variant="outline" size="sm" onClick={() => closeGitOrder(entry.id)}>
                                  Close & create invoice
                                </Button>
                                <Button className="w-full gap-2" variant="outline" size="sm" onClick={() => deleteGitOrder(entry.id)}>
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="hidden sm:block overflow-x-auto">
              <Table className="min-w-[860px]">
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Misc</TableHead>
                    <TableHead className="text-right">Taxes</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    {developerUnlocked && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workorders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={developerUnlocked ? 10 : 9} className="py-6 text-center text-muted-foreground">
                        No Git orders yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    workorders.map((entry) => {
                      const commitCount = (entry.commitShas ?? []).length;
                      const unitRate = typeof entry.commitRate === 'number' ? entry.commitRate : defaultCommitRate;
                      const miscValue = typeof entry.misc === 'number' ? entry.misc : 0;
                      const taxesValue = typeof entry.taxes === 'number' ? entry.taxes : 0;
                      const totalValue = commitCount * unitRate + miscValue + taxesValue;
                      const hasInvoice = !!entry.invoiceJournalEntryId;
                      const isClosed = hasInvoice || entry.stage === 'invoiced';

                      return (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm">
                            {Number.isNaN(new Date(entry.date).getTime()) ? '—' : format(new Date(entry.date), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell className="text-right font-medium">{Number.isFinite(entry.hours) ? entry.hours.toFixed(2) : '—'}</TableCell>
                          <TableCell className="max-w-[300px] truncate" title={entry.description}>
                            {entry.description}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">{commitCount || '—'}</TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">
                            {commitCount > 0 ? unitRate.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">
                            {miscValue ? miscValue.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">
                            {taxesValue ? taxesValue.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground">
                            {commitCount > 0 || miscValue || taxesValue ? totalValue.toFixed(2) : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {isClosed ? 'Closed' : 'Draft'}
                          </TableCell>
                          {developerUnlocked && (
                            <TableCell className="text-right">
                              {isClosed ? (
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline" size="sm" onClick={() => openInvoiceForGitOrder(entry.id)}>
                                    Open invoice
                                  </Button>
                                  <Button variant="outline" size="icon" aria-label="Delete Git order" onClick={() => deleteGitOrder(entry.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex justify-end gap-2">
                                  <Button variant="ghost" size="sm" className="gap-2" onClick={() => openWorkorderCommitsDialog(entry.id)}>
                                    <PencilLine className="w-4 h-4" />
                                    Edit
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => closeGitOrder(entry.id)}>
                                    Close & create invoice
                                  </Button>
                                  <Button variant="outline" size="icon" aria-label="Delete Git order" onClick={() => deleteGitOrder(entry.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {developerUnlocked ? (
          <div className="mb-4 rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
            <p className="text-sm font-medium">Add invoice</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input type="date" value={journalDate} onChange={(event) => setJournalDate(event.target.value)} />
              <div className="space-y-2">
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={journalCustomerId}
                  onChange={(event) => setJournalCustomerId(event.target.value)}
                >
                  <option value="">Select customer</option>
                  <option value={INTERNAL_CUSTOMER_ID}>Internal</option>
                  {visibleCustomers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                  <option value={CREATE_CUSTOMER_VALUE}>Create customer…</option>
                </select>
                {journalCustomerId === INTERNAL_CUSTOMER_ID ? (
                  <p className="text-xs text-muted-foreground">Internal = liability held by HappyApps for eventual recovery.</p>
                ) : null}
                {journalCustomerId === CREATE_CUSTOMER_VALUE ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="New customer name"
                      value={newCustomerName}
                      onChange={(event) => setNewCustomerName(event.target.value)}
                    />
                    <Button type="button" variant="outline" onClick={handleCreateCustomer} disabled={!newCustomerName.trim()}>
                      Create
                    </Button>
                  </div>
                ) : null}
              </div>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Amount"
                value={journalAmount}
                onChange={(event) => setJournalAmount(event.target.value)}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Misc"
                value={journalMisc}
                onChange={(event) => setJournalMisc(event.target.value)}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Taxes"
                value={journalTaxes}
                onChange={(event) => setJournalTaxes(event.target.value)}
              />
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={journalType}
                onChange={(event) => setJournalType(event.target.value as JournalEntry['entryType'])}
              >
                <option value="retainer">Retainer</option>
                <option value="service-charge">Service charge</option>
                <option value="initialization-webapp-build">Initialization WebApp Build</option>
              </select>
            </div>
            <Input placeholder="Notes (optional)" value={journalNotes} onChange={(event) => setJournalNotes(event.target.value)} />
            <div className="flex justify-end">
              <Button onClick={handleAddJournalEntry} className="w-full sm:w-auto gap-2">
                <PencilLine className="w-4 h-4" />
                Add invoice
              </Button>
            </div>
          </div>
        ) : (
          <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border/60 bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">Developer passcode required to add or edit invoices.</p>
            <Button variant="outline" className="gap-2" onClick={onOpenDeveloperDialog}>
              <LockKeyhole className="w-4 h-4" />
              Unlock
            </Button>
          </div>
        )}

        <div className="rounded-lg border border-border overflow-hidden">
          <div className="sm:hidden">
            {invoiceEntries.length === 0 ? (
              <div className="p-4">
                <div className="flex flex-col items-center gap-3 text-center">
                  <p className="text-sm text-muted-foreground">No invoices are visible.</p>
                  {!developerUnlocked ? (
                    <>
                      <p className="text-xs text-muted-foreground">
                        While locked, invoices are filtered to entries created on this device. Unlock with the developer passcode to view all invoices.
                      </p>
                      <Button variant="outline" size="sm" className="w-full gap-2" onClick={onOpenDeveloperDialog}>
                        <LockKeyhole className="w-4 h-4" />
                        Unlock
                      </Button>
                    </>
                  ) : !isFirebaseConfigured ? (
                    <p className="text-xs text-muted-foreground">
                      Firestore sync is not configured on this build, so invoices saved elsewhere will not appear here.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Add an invoice above to get started.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {invoiceEntries.map((entry) => (
                  <div key={entry.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {Number.isNaN(new Date(entry.date).getTime()) ? '—' : format(new Date(entry.date), 'MMM d, yyyy')}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatJournalEntryType(entry.entryType)} · {entry.customer}
                        </p>
                        <p className="mt-2 text-xs text-muted-foreground break-words">{entry.notes || '—'}</p>
                      </div>

                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold">${entry.amount.toFixed(2)}</p>
                      </div>
                    </div>

                    {developerUnlocked && (
                      <div className="mt-3">
                        <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => openInvoiceEditor(entry.id)}>
                          <PencilLine className="w-4 h-4" />
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <Table className="min-w-[860px]">
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Notes</TableHead>
                  {developerUnlocked && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoiceEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={developerUnlocked ? 6 : 5} className="py-6">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <p className="text-sm text-muted-foreground">No invoices are visible.</p>
                        {!developerUnlocked ? (
                          <>
                            <p className="text-xs text-muted-foreground max-w-[520px]">
                              While locked, invoices are filtered to entries created on this device. Unlock with the developer passcode to view all invoices.
                            </p>
                            <Button variant="outline" size="sm" className="gap-2" onClick={onOpenDeveloperDialog}>
                              <LockKeyhole className="w-4 h-4" />
                              Unlock
                            </Button>
                          </>
                        ) : !isFirebaseConfigured ? (
                          <p className="text-xs text-muted-foreground max-w-[520px]">
                            Firestore sync is not configured on this build, so invoices saved elsewhere will not appear here.
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground max-w-[520px]">Add an invoice above to get started.</p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoiceEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">
                        {Number.isNaN(new Date(entry.date).getTime()) ? '—' : format(new Date(entry.date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="capitalize">{formatJournalEntryType(entry.entryType)}</TableCell>
                      <TableCell>{entry.customer}</TableCell>
                      <TableCell className="text-right font-medium">${entry.amount.toFixed(2)}</TableCell>
                      <TableCell className="max-w-[220px] truncate" title={entry.notes}>
                        {entry.notes || '—'}
                      </TableCell>
                      {developerUnlocked && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="gap-2" onClick={() => openInvoiceEditor(entry.id)}>
                            <PencilLine className="w-4 h-4" />
                            Edit
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {invoiceEntries.length > 0 && (
          <div className="mt-3 flex justify-end text-sm text-muted-foreground">
            Total invoices: <span className="ml-2 font-semibold text-foreground">${totalInvoiceAmount.toFixed(2)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
